import { call, takeEvery, put, select } from 'redux-saga/effects'
import { lmicentralUserActions, LmiCentralUserActionTypes } from '@lmicentral/state/user/actions'
import commonServices, {
    verifyUser,
    getBillingSubscriptions,
    getBillingInfo,
    getExistingPaymentDetails,
} from '@common/services'
import { getLabelContent, getUserState, getContentState } from '@lmicentral/state/selectors'
import { authClass, processExistingBillingInfo } from '@common/utils'
import { trackAbandonForm } from '@lmicentral/utils'
import { LmiCentralUserState } from '@lmicentral/state/user/state'
import {
    ExistingBillingInfoType,
    ExistingUserInfoType,
    ExistingAccountInfoType,
    ExistingPaymentInfoType,
} from '@common/state/user/state'
import { lmicentralCheckoutActions } from '@lmicentral/state/checkout/actions'
import { lmicentralBuyFormActions } from '@lmicentral/state/buy-form/actions'
import { LmiCentralBuyFormData } from '@lmicentral/state/buy-form/state'
import {
    PREFIX,
    TimePeriod,
    BILLING_TYPES,
    PRODUCT_TYPES,
    PRODUCT_SKU,
    COUNTRIES,
    SC_SITE,
    PRODUCT_FAMILY_NAME,
} from '@common/constants'
import { LabelContent } from '@lmicentral/state/label-content'
import { LMICENTRAL_USER_TYPES, LMICENTRAL_USER_TYPES_RESPONSE } from '@lmicentral/constants'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { isEmpty, partition } from 'lodash-es'
import { BillingDetailedSubscriptionPlan } from '@common/state/billing/state'
import { accountActions } from '@common/state/account/actions'
import { getCountryFromCookie, setCountryInCookie } from '@common/utils/cookie-utils'
import { GenericResponse } from '@common/state/generic-response/generic-response'
import { contentActions } from '@lmicentral/state/content/actions'
import { LmiCentralContentState } from '@lmicentral/state/content/state'
import { FEATURE_FLAGS } from '@common/constants/featureFlags'

function* userLoggedInSaga() {
    try {
        yield put(lmicentralUserActions.setLoadingExistingInformation(true))
        const {
            loggedIn: { accessToken },
        }: LmiCentralUserState = yield select(getUserState)
        const { paymentMethodErrorMessage, disabledUserError }: LabelContent = yield select(getLabelContent)

        // clear previous session on the server
        yield call(commonServices.clearUserSession)
        // get user info
        // eslint-disable-next-line camelcase
        const response: GenericResponse = yield call(
            verifyUser,
            accessToken?.access_token || '',
            PRODUCT_FAMILY_NAME.LMICENTRAL
        )
        if (response && !isEmpty(response.data)) {
            const {
                User: { UserName, Name, UserKey },
                AccountKey,
                isBanned,
                hasGroupSubscription,
            } = response.data

            if (hasGroupSubscription) {
                trackAbandonForm()
                window.location.href = process.env.ADMIN_LMICENTRAL_URL as string
            }

            const userInfo: ExistingUserInfoType = {
                UserName,
                Name,
                UserKey,
                AccountKey,
            }

            const buyFormData: LmiCentralBuyFormData = {
                FirstName: Name?.GivenName,
                LastName: Name?.FamilyName,
                Email: UserName,
                Street1: '',
                City: '',
                ZipCode: '',
                StateCode: '',
                CountryCode: getCountryFromCookie() || COUNTRIES.US,
                ContactPhone: '',
                Password: '',
                BillingFirstName: '',
                BillingLastName: '',
                ExpirationDate: '',
                TaxNumber: '',
                TaxType: '',
                CompanyName: '',
            }

            // return error message if user is banned
            if (isBanned === true) {
                yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.BANNED))
                yield put(lmicentralUserActions.setLoadingExistingInformation(false))
                yield put(
                    fatalErrorActions.setFatalError(
                        true,
                        { fatalErrorMessage: disabledUserError, trackErrorMessage: disabledUserError },
                        true
                    )
                )
                return
            }

            // USER_IDENTITY_NOT_FOUND implies that user has neither boss nor identity account
            // This is the state returned when a token has been invalidated, I dont know
            // if there is another use case or not
            if (response.data.Code === LMICENTRAL_USER_TYPES_RESPONSE.USER_IDENTITY_NOT_FOUND) {
                yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.NEW))
                yield put(lmicentralUserActions.userLogout())
                return
            }

            // set up and display existing user account information
            yield put(lmicentralUserActions.setExistingUserInformation(userInfo))

            // USER_BOSS_NOT_FOUND implies that user has an identity account
            if (response.data.Code === LMICENTRAL_USER_TYPES_RESPONSE.USER_BOSS_NOT_FOUND) {
                yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.IDENTITY_ONLY))
                yield put(lmicentralBuyFormActions.updateInitialFormState(buyFormData))
                yield put(lmicentralUserActions.setLoadingExistingInformation(false))
                return
            }

            // #region attempt to set up existing billing info
            const billingInfoRequest: GenericResponse = yield call(getBillingInfo)
            if (billingInfoRequest && !isEmpty(billingInfoRequest.data)) {
                // We get company name same as the email without the `@` in case we don't provide in the first place.
                // This is a known behavior for GTC. So, we check for GTR, we check if that's what we got and don't show the value
                const { email, companyName } = billingInfoRequest.data
                if (email.replace('@', '').replace('_', '') === companyName) {
                    billingInfoRequest.data.companyName = ''
                }

                const processedBillingData = processExistingBillingInfo(billingInfoRequest.data)
                const billingInfo: ExistingBillingInfoType = processedBillingData
                const currentCountry = getCountryFromCookie() || COUNTRIES.US

                if (billingInfo.state === 'XX') {
                    billingInfo.state = ''
                    yield put(lmicentralBuyFormActions.setStatesOptional(true))
                }

                // If address IS set, but GeoIP is different from existing user country,
                // reload such that buy form country matches that of GeoIP cookie
                if (billingInfo.addressLine1) {
                    if (billingInfo.country !== currentCountry) {
                        setCountryInCookie(billingInfo.country)
                        window.location.reload()
                    }
                } else {
                    billingInfo.country = currentCountry
                }

                billingInfo.firstName = Name.GivenName
                billingInfo.lastName = Name.FamilyName
                buyFormData.Street1 = billingInfo.addressLine1
                buyFormData.City = billingInfo.city
                buyFormData.CountryCode = billingInfo.country
                buyFormData.ZipCode = billingInfo.postalCode
                buyFormData.StateCode = billingInfo.state
                buyFormData.ContactPhone = billingInfo.phoneNumber
                buyFormData.TaxNumber = billingInfo.taxNumber || ''
                buyFormData.TaxType = billingInfo.federalTaxType || ''

                // all five fields must contain values in order to set existing billing information
                if (buyFormData.Street1 && buyFormData.City && buyFormData.CountryCode && buyFormData.ZipCode) {
                    yield put(lmicentralUserActions.setExistingBillingInformation(billingInfo))
                }
            }
            yield put(lmicentralBuyFormActions.updateInitialFormState(buyFormData))
            // #endregion

            // #region attempt to set up existing payment info
            const paymentInfoRequest: GenericResponse = yield call(getExistingPaymentDetails)
            if (paymentInfoRequest && !isEmpty(paymentInfoRequest.data)) {
                const currentDate = new Date()
                const currentYear = currentDate.getFullYear()
                const currentMonth = currentDate.getMonth()
                const ccInfo = paymentInfoRequest.data[0].creditCard
                if (
                    ccInfo.expirationYear < currentYear ||
                    (ccInfo.expirationYear === currentYear && ccInfo.expirationMonth < currentMonth)
                ) {
                    yield put(
                        fatalErrorActions.setFatalError(
                            true,
                            {
                                fatalErrorMessage: paymentMethodErrorMessage,
                                trackErrorMessage: paymentMethodErrorMessage,
                            },
                            true
                        )
                    )
                }
                const paymentInfo: ExistingPaymentInfoType = {
                    last4Digits: ccInfo.last4Digits,
                }
                yield put(lmicentralUserActions.setExistingPaymentDetails(paymentInfo))
            }
            // #endregion

            // if subscription is empty, mark as expired-user
            const subscriptionRequest: GenericResponse = yield call(getBillingSubscriptions)
            if (isEmpty(subscriptionRequest?.data)) {
                yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.EXPIRED))
                yield put(lmicentralUserActions.setLoadingExistingInformation(false))
                return
            }

            const featureFlags: { data: { flags: string[] } } = yield call(
                commonServices.getFeatureFlags,
                SC_SITE.LOGMEIN
            )

            const useNewLmiCentralSkus = featureFlags?.data?.flags?.includes(FEATURE_FLAGS.LMIPRO_USE_NEW_SKUS)

            /* LOGIC CATCH FOR EXISTING USERS
              if feature flag is on, and...

              https://jira.ops.expertcity.com/browse/WAE1-2522
              If the user is subscribed... | ...they could upgrade to...
              Basic v1 | Standard v2
              Standard v1 | N/A
              Remote Support | Standard v2
              Standard v2 | N/A
            */
            const existingSubscriptionSkus =
                subscriptionRequest?.data?.map(({ productSku }: { productSku: PRODUCT_SKU }) => productSku) ?? []
            const skuFilter: string[] = []

            if (useNewLmiCentralSkus) {
                if (
                    !existingSubscriptionSkus.includes(PRODUCT_SKU.LMIPRO_STANDARD_NAME) &&
                    !existingSubscriptionSkus.includes(PRODUCT_SKU.LMIPRO_STANDARD_NAMED_V2)
                ) {
                    if (existingSubscriptionSkus.includes(PRODUCT_SKU.LMIPRO_BASIC_NAMED)) {
                        skuFilter.push(PRODUCT_SKU.LMIPRO_BASIC_NAMED)
                    } else {
                        skuFilter.push(PRODUCT_SKU.LMIPRO_REMOTE_SUPPORT_NAMED_V2)
                    }
                    skuFilter.push(PRODUCT_SKU.LMIPRO_STANDARD_NAMED_V2)
                } else if (existingSubscriptionSkus.includes(PRODUCT_SKU.LMIPRO_STANDARD_NAME)) {
                    skuFilter.push(PRODUCT_SKU.LMIPRO_STANDARD_NAME)
                } else {
                    skuFilter.push(PRODUCT_SKU.LMIPRO_STANDARD_NAMED_V2)
                }
            }

            const content: LmiCentralContentState = yield select(getContentState)

            const filterPlans = useNewLmiCentralSkus
                ? (sku: PRODUCT_SKU) => skuFilter.includes(sku)
                : (sku: PRODUCT_SKU) => [PRODUCT_SKU.LMICENTRAL_BASIC].includes(sku)

            yield put(
                contentActions.setContent(
                    content.contentData,
                    content.labelContent,
                    content.allPlanData.filter((plan) => filterPlans(plan.sKU as PRODUCT_SKU)) as [],
                    content.allPlanData as []
                )
            )

            // set account subscription state
            yield put(accountActions.setAccountSubscriptions(subscriptionRequest.data))

            // partition subscriptions into two arrays - one for GTC and another for GTR
            const [lmicentralSubscriptions] = partition(
                subscriptionRequest.data,
                (plan: BillingDetailedSubscriptionPlan) => {
                    return plan.productSku.startsWith(PREFIX.GTR)
                }
            )

            const lmicentralTrialSubscription = lmicentralSubscriptions.find(
                (plan: BillingDetailedSubscriptionPlan) => plan.planType === PRODUCT_TYPES.TRIAL
            )

            // #region determine buy flow user state
            const selectedPlan = lmicentralSubscriptions?.find((plan: BillingDetailedSubscriptionPlan) => {
                if (useNewLmiCentralSkus) {
                    return (
                        plan.productSku === PRODUCT_SKU.LMIPRO_BASIC_NAMED ||
                        plan.productSku === PRODUCT_SKU.LMIPRO_STANDARD_NAME ||
                        plan.productSku === PRODUCT_SKU.LMIPRO_REMOTE_SUPPORT_NAMED_V2 ||
                        plan.productSku === PRODUCT_SKU.LMIPRO_REMOTE_ACCESS ||
                        plan.productSku === PRODUCT_SKU.LMIPRO_STANDARD_NAMED_V2
                    )
                }
                return (
                    plan.productSku === PRODUCT_SKU.LMIPRO_BASIC_NAMED ||
                    plan.productSku === PRODUCT_SKU.LMIPRO_STANDARD_NAME
                )
            })
            if (selectedPlan) {
                const {
                    productName,
                    paymentPlan,
                    startDate,
                    endDate,
                    currency,
                    renewalPrice,
                    autorenew,
                    productSku,
                    subscriptionId,
                    status,
                    planType,
                } = selectedPlan ?? {}

                const overallQuantity = lmicentralSubscriptions.reduce(
                    (accumulator: number, subItem: ExistingAccountInfoType) => {
                        if (subItem.productSku === productSku) {
                            return accumulator + subItem.quantity
                        }
                        return accumulator
                    },
                    0
                )

                const accountInfo: ExistingAccountInfoType = {
                    productName,
                    paymentPlan,
                    startDate,
                    endDate,
                    currency,
                    renewalPrice,
                    autorenew,
                    productSku,
                    subscriptionId,
                    status,
                    quantity: overallQuantity,
                    planType,
                }

                yield put(lmicentralUserActions.setExistingAccountInformation(accountInfo))
                yield put(lmicentralCheckoutActions.setSelectedPlan(productSku))
                yield put(
                    lmicentralCheckoutActions.setBillingFrequency(
                        paymentPlan === BILLING_TYPES.ANNUAL ? TimePeriod.Year : TimePeriod.Month
                    )
                )
                // if user already has the selected plan, mark as add-on flow
                yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.ADDON))
                yield put(lmicentralCheckoutActions.setSelectedAddons([]))
            } else if (lmicentralTrialSubscription) {
                // otherwise, if user has any LmiPro trial subscription, mark as trialer
                yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.TRIALER))
            } else {
                // otherwise, since user has at least one subscription, mark as cross product
                yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.CROSSPRODUCT))
            }
            // #endregion

            yield put(lmicentralUserActions.setLoadingExistingInformation(false))
        }
    } catch (e) {
        yield put(lmicentralUserActions.setUserType(LMICENTRAL_USER_TYPES.NEW))
        yield put(lmicentralUserActions.setLoadingExistingInformation(false))
        yield call(authClass().logout)
    }
}

function* initializeLoggedInSaga() {
    yield takeEvery(LmiCentralUserActionTypes.GET_LOGGED_IN_USER_STATE, userLoggedInSaga)
}

export default initializeLoggedInSaga
